import { Form, Input } from 'antd';
import ItemModifierOptionRecipes from 'containers/MenuManagement/Overview/MenuItemsTable/ItemsTable/ItemDetailModal/ItemModifierOptionRecipes';
import FormItem from 'components/FormItem';
import FormLayout from 'components/FormLayout';
import SwitchInput from 'components/SwitchInput';
import React, { useContext, useMemo } from 'react';
import {
  collectModifierOptionIds,
  isRecipesEditable,
  pruneModifierOptionRecipes,
  resolveAttachedModifiers,
  validateModifierOptionRecipes,
} from 'utils/menu';
import { MenuContext } from '../../constants';
import AdditionalInfo from './AdditionalInfo';
import Modifiers from './Modifiers';
import Recipes from './Recipes';
import Sizes from './Sizes';
import Unit from './Unit';

function ModifiersField({ value = [], onChange }) {
  const { modifiers: allModifiers } = useContext(MenuContext);
  const form = Form.useFormInstance();

  const handleChange = (nextModifiers) => {
    const resolved = resolveAttachedModifiers(nextModifiers, allModifiers);
    const allowedOptionIds = new Set(collectModifierOptionIds(resolved, allModifiers));
    const prunedRecipes = pruneModifierOptionRecipes(
      form.getFieldValue('modifier_option_recipes'),
      allowedOptionIds,
    );
    onChange?.(nextModifiers);
    form.setFieldValue('modifier_option_recipes', prunedRecipes);
  };

  return <Modifiers value={value} onChange={handleChange} />;
}

function ItemFields({ disabled, migrationDriver }) {
  const { isGlobal, isCatering, modifiers: allModifiers } = useContext(MenuContext);
  const form = Form.useFormInstance();
  const recipesDisabled = !isRecipesEditable(isGlobal, migrationDriver);
  const attachedModifiers = Form.useWatch('modifiers', form) || [];
  const recipeLines = Form.useWatch('recipe_lines', form) || [];
  const modifierOptionRecipes = Form.useWatch('modifier_option_recipes', form) || [];

  const itemModifiers = useMemo(
    () => resolveAttachedModifiers(attachedModifiers, allModifiers),
    [attachedModifiers, allModifiers],
  );

  const modifierRecipeValidation = useMemo(
    () => validateModifierOptionRecipes(modifierOptionRecipes, recipeLines),
    [modifierOptionRecipes, recipeLines],
  );

  return (
    <>
      <FormLayout.Section divider={false}>
        <FormItem name="name" label="Name" rules={[{ required: true }]} fullWidth>
          <Input placeholder="Item name" disabled={disabled} />
        </FormItem>
        <FormItem name="is_active" boolean fullWidth hidden={isGlobal}>
          <SwitchInput
            label="Mark as available"
            description="Customers can view item and select it."
            className="flex-row-reverse w-full justify-between"
          />
        </FormItem>
      </FormLayout.Section>

      <Unit disabled={disabled} />
      <Sizes disabled={disabled} />
      <AdditionalInfo disabled={disabled} />
      {isCatering && (
        <Form.Item name="recipe_lines" noStyle>
          <Recipes disabled={recipesDisabled} />
        </Form.Item>
      )}
      <Form.Item name="modifiers" noStyle>
        <ModifiersField />
      </Form.Item>
      {isCatering && (
        <Form.Item name="modifier_option_recipes" noStyle>
          <ItemModifierOptionRecipes
            modifiers={itemModifiers}
            allModifiers={allModifiers}
            value={modifierOptionRecipes}
            onChange={(next) => form.setFieldValue('modifier_option_recipes', next)}
            disabled={recipesDisabled}
            itemRecipeLines={recipeLines}
            validationErrors={modifierRecipeValidation.errors}
          />
        </Form.Item>
      )}
    </>
  );
}

export default ItemFields;
